import { PlayIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import type { AgentPageResponse, AgentRunSummary } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { toast } from 'src/components/ui/toast';
import { useCan } from 'src/hooks/useCan';
import { useLaunchAgentRun } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { AgentStopButton } from '../../agent-monitor/components/AgentStopButton';

/** Server error message when the mutation surfaced one, else the fallback. */
function mutationErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * Run / Stop for an agent page. Run goes through the durable launcher
 * (`agentDefinitions.launchRun`): the run row exists when the call returns and
 * a Temporal worker owns execution, so it survives a web restart and Stop can
 * cancel that execution directly. Orchestrated agents are launchable too —
 * their start conditions still gate whether work happens.
 */
export function AgentPageRunControls({
  page,
  latestRun,
  orchestrated,
}: {
  page: AgentPageResponse;
  latestRun: AgentRunSummary | null;
  /** Task-management agent: Stop can wait for the current task. */
  orchestrated: boolean;
}) {
  const gt = useGT();
  const [canExecute] = useCan(Action.Execute, Subject.BackgroundAgent);
  // Stopping a run is gated on Update server-side, not Execute.
  const [canStop] = useCan(Action.Update, Subject.BackgroundAgent);
  const launch = useLaunchAgentRun();

  if (!canExecute) return null;

  const isRunning = latestRun?.status === 'running';
  const inactive = !page.agentDefinitionIsActive;

  async function handleRun() {
    try {
      await launch.mutateAsync({ agentDefinitionId: page.agentDefinitionId });
      toast.success(
        gt('Run started', {
          context: 'toast after launching an agent run from an agent page',
        }),
      );
    } catch (error) {
      toast.error(
        mutationErrorMessage(
          error,
          gt('Failed to start the agent', {
            context: 'toast when launching an agent run failed',
          }),
        ),
      );
    }
  }

  const runButton = (
    <Button
      size="sm"
      onClick={() => void handleRun()}
      disabled={isRunning || inactive || launch.isPending}
    >
      <PlayIcon className="mr-1 h-4 w-4" />
      {isRunning ? (
        <T context="agent page control while a run is in progress">Running</T>
      ) : (
        <T context="button to launch a run of the page's agent">Run</T>
      )}
    </Button>
  );

  return (
    <div className="flex items-center gap-2">
      {inactive ? (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Span wrapper: disabled buttons don't fire the hover events the tooltip needs */}
            <span tabIndex={0}>{runButton}</span>
          </TooltipTrigger>
          <TooltipContent>
            <T context="explains why an agent page cannot launch a run">
              This agent is inactive or archived, so it cannot be run.
            </T>
          </TooltipContent>
        </Tooltip>
      ) : (
        runButton
      )}

      {canStop && (
        <AgentStopButton
          latestRun={latestRun}
          supportsStopAfterTask={orchestrated}
        />
      )}
    </div>
  );
}
